/**
 * Budget preparation (422477): one budget per financial year — an amount per income/expense account,
 * cost centre and period. Prepared as a Draft, approved, and revised (back to Draft) when it must change.
 * Typing a row's Total spreads it evenly across the periods.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Calculator, Plus, Trash2, Save, BadgeCheck, RotateCcw, AlertTriangle, CheckCircle2 } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { financialYearApi } from '@/api/financialYear.api';
import { budgetApi, type Budget, type BudgetRow } from '@/api/budget.api';

const errorText = (e: unknown) =>
  (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'That did not work.';
const money = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const cell = 'px-1 py-1 border-b border-slate-100 dark:border-slate-800';
const input = 'w-full px-1.5 py-1 text-xs border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white disabled:bg-transparent disabled:border-transparent';

/** Splits a total evenly across n periods, putting the rounding remainder on the last one. */
export const spread = (total: number, n: number): number[] => {
  const each = Math.floor((total / n) * 100) / 100;
  return Array.from({ length: n }, (_, i) => (i === n - 1 ? Math.round((total - each * (n - 1)) * 100) / 100 : each));
};

export const BudgetPage = () => {
  const { hasPermission } = usePermission();
  const canEdit    = hasPermission(Permissions.BUD_BUDGET_EDIT);
  const canApprove = hasPermission(Permissions.BUD_BUDGET_APPROVE);

  const [years, setYears]   = useState<{ yearCode: string; isCurrent: boolean }[]>([]);
  const [year, setYear]     = useState('');
  const [budget, setBudget] = useState<Budget | null>(null);
  const [rows, setRows]     = useState<BudgetRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy]     = useState<string | null>(null);
  const [error, setError]   = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [dirty, setDirty]   = useState(false);

  useEffect(() => {
    financialYearApi.getList({ pageSize: 50 }).then(r => {
      const list = r.data?.data?.items ?? [];
      setYears(list);
      setYear((list.find(y => y.isCurrent) ?? list[0])?.yearCode ?? '');
    }).catch(() => setYears([]));
  }, []);

  const show = (b: Budget) => { setBudget(b); setRows(b.rows); setDirty(false); };

  const load = useCallback(async () => {
    if (!year) return;
    setLoading(true); setError(null); setNotice(null);
    try { show(await budgetApi.get(year)); }
    catch (e) { setBudget(null); setError(errorText(e)); }
    finally { setLoading(false); }
  }, [year]);

  useEffect(() => { void load(); }, [load]);

  const act = async (what: string, call: () => Promise<Budget>, done: string) => {
    setBusy(what); setError(null); setNotice(null);
    try { show(await call()); setNotice(done); }
    catch (e) { setError(errorText(e)); }
    finally { setBusy(null); }
  };

  const n = budget?.periods.length ?? 0;
  const editable = canEdit && budget?.status !== 'APPROVED';
  const update = (i: number, patch: Partial<BudgetRow>) => {
    setRows(rs => rs.map((r, k) => (k === i ? { ...r, ...patch } : r)));
    setDirty(true);
  };
  const addRow = () => {
    setRows(rs => [...rs, { accountId: '', costCenterId: '', amounts: Array<number>(n).fill(0) }]);
    setDirty(true);
  };

  const accountName = useMemo(() => new Map(budget?.accounts.map(a => [a.id, a]) ?? []), [budget]);
  const periodTotals = useMemo(() => Array.from({ length: n }, (_, p) => rows.reduce((s, r) => s + (r.amounts[p] ?? 0), 0)), [rows, n]);
  const incomplete = rows.some(r => !r.accountId || !r.costCenterId);

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4 flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[16rem]">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <Calculator size={20} className="text-signal" /> Budget
            {budget?.status && (
              <span className={`text-xs font-medium px-2 py-0.5 rounded ${budget.status === 'APPROVED'
                ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                : 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'}`}>
                {budget.status === 'APPROVED' ? 'Approved' : 'Draft'}
              </span>
            )}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            Income and expenditure by account, cost centre and period. Enter positive figures; type a Total to spread it evenly.
          </p>
        </div>
        <select value={year} onChange={e => setYear(e.target.value)} className={`${input} !w-36 !text-sm !py-1.5`}>
          {years.map(y => <option key={y.yearCode} value={y.yearCode}>{y.yearCode}</option>)}
        </select>
        {editable && (
          <Button size="sm" leftIcon={<Save size={14} />} loading={busy === 'save'} disabled={!dirty || incomplete}
            onClick={() => void act('save', () => budgetApi.save(year, budget?.name ?? null, rows), 'Budget saved.')}>
            Save
          </Button>
        )}
        {canApprove && budget?.status === 'DRAFT' && (
          <Button size="sm" variant="outline" leftIcon={<BadgeCheck size={14} />} loading={busy === 'approve'} disabled={dirty}
            onClick={() => void act('approve', () => budgetApi.approve(year), 'Budget approved. Actual vs Budget now measures against it.')}>
            Approve
          </Button>
        )}
        {canApprove && budget?.status === 'APPROVED' && (
          <Button size="sm" variant="outline" leftIcon={<RotateCcw size={14} />} loading={busy === 'revise'}
            onClick={() => void act('revise', () => budgetApi.revise(year), 'Budget reopened as a draft for revision.')}>
            Revise
          </Button>
        )}
      </div>

      <div className="flex-1 overflow-auto p-6 space-y-4">
        {error && (
          <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" /> {error}
          </div>
        )}
        {notice && (
          <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-sm text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 size={16} className="mt-0.5 shrink-0" /> {notice}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
        ) : budget && (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto">
            <table className="text-xs min-w-full">
              <thead className="bg-slate-50 dark:bg-slate-800/60">
                <tr className="text-left text-slate-500 dark:text-gray-400 uppercase">
                  <th className="px-2 py-2 min-w-[14rem]">Account</th>
                  <th className="px-2 py-2 min-w-[10rem]">Cost Centre</th>
                  {budget.periods.map(p => <th key={p.periodNumber} className="px-2 py-2 min-w-[6.5rem] text-right" title={p.periodName}>{p.periodName.slice(0, 3)}</th>)}
                  <th className="px-2 py-2 min-w-[8rem] text-right">Total</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const total = r.amounts.reduce((s, a) => s + a, 0);
                  return (
                    <tr key={i}>
                      <td className={cell}>
                        <select className={input} value={r.accountId} disabled={!editable} onChange={e => update(i, { accountId: e.target.value })}>
                          <option value="">Choose…</option>
                          {['Income', 'Expenditure'].map(g => (
                            <optgroup key={g} label={g}>
                              {budget.accounts.filter(a => a.group === g).map(a => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
                            </optgroup>
                          ))}
                        </select>
                      </td>
                      <td className={cell}>
                        <select className={input} value={r.costCenterId} disabled={!editable} onChange={e => update(i, { costCenterId: e.target.value })}>
                          <option value="">Choose…</option>
                          {budget.costCenters.map(c => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}
                        </select>
                      </td>
                      {r.amounts.map((a, p) => (
                        <td key={p} className={cell}>
                          <input type="number" min={0} step="0.01" className={`${input} text-right`} value={a || ''} disabled={!editable}
                            onChange={e => update(i, { amounts: r.amounts.map((x, k) => (k === p ? Number(e.target.value) || 0 : x)) })} />
                        </td>
                      ))}
                      <td className={cell}>
                        <input type="number" min={0} step="0.01" className={`${input} text-right font-semibold`} value={total || ''} disabled={!editable}
                          title="Type a yearly total to spread it evenly across the periods"
                          onChange={e => update(i, { amounts: spread(Number(e.target.value) || 0, n) })} />
                      </td>
                      <td className={cell}>
                        {editable && (
                          <button onClick={() => { setRows(rs => rs.filter((_, k) => k !== i)); setDirty(true); }}
                            className="p-1 text-slate-400 hover:text-red-600" title="Remove row"><Trash2 size={13} /></button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr><td colSpan={n + 4} className="px-4 py-10 text-center text-slate-500">
                    No budget for {year} yet.{editable ? ' Add a row to start.' : ''}
                  </td></tr>
                )}
              </tbody>
              {rows.length > 0 && (
                <tfoot className="bg-slate-50 dark:bg-slate-800/60 font-semibold">
                  <tr>
                    <td className="px-2 py-2" colSpan={2}>
                      Total · income {money(rows.filter(r => accountName.get(r.accountId)?.group === 'Income').reduce((s, r) => s + r.amounts.reduce((x, y) => x + y, 0), 0))}
                      {' · '}expenditure {money(rows.filter(r => accountName.get(r.accountId)?.group === 'Expenditure').reduce((s, r) => s + r.amounts.reduce((x, y) => x + y, 0), 0))}
                    </td>
                    {periodTotals.map((t, p) => <td key={p} className="px-2 py-2 text-right tabular-nums">{money(t)}</td>)}
                    <td className="px-2 py-2 text-right tabular-nums">{money(periodTotals.reduce((s, t) => s + t, 0))}</td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
            {editable && (
              <div className="px-3 py-2 border-t border-slate-100 dark:border-slate-700">
                <Button size="sm" variant="outline" leftIcon={<Plus size={14} />} onClick={addRow}>Add row</Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
